import { Close, CompareArrows, Search } from "@mui/icons-material";
import { Box, Button, Divider, InputBase, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren } from "react";
import { useLocalStorage } from "react-use";
import { useTemporaryCrmLiteRedirect } from "../../../../../State/Hooks/crmLiteHooks";
import { toggleLoading, updateClient } from "../../../../../State/Slices/SelectedClientSlice";
import { useAppDispatch } from "../../../../../State/Hooks/reduxHooks";
import { IClient } from "../../../../../Typings/Client";

export const ClientSelector = ({
  searchVal,
  setSearchVal,
  filteredClients,
  handleCloseUserMenu,
  handleCloseClientsMenu,
}: PropsWithChildren<{
  searchVal: string;
  setSearchVal: (val: string) => void;
  filteredClients: IClient[];
  handleCloseUserMenu: () => void;
  handleCloseClientsMenu: () => void;
}>) => {
  useTemporaryCrmLiteRedirect(); // CRM-501 - Redirect to Dashboard until CRM Lite features are implemented
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [lastClientId, setLastClientId] = useLocalStorage("lastClientId", "");
  const handleSetClient = async (client: IClient) => {
    if (client.clientId !== lastClientId) {
      setLastClientId(client.clientId);
    }
    dispatch(updateClient(client));
    dispatch(toggleLoading(false));
    await queryClient.invalidateQueries();
  };
  return (
    <Box sx={{ width: "300px", maxHeight: "50vh", overflow: "auto" }}>
      <Tooltip title="Close Menu" placement="top-start" arrow>
        <Button
          data-test="client-menu-returntomain"
          onClick={handleCloseClientsMenu}
          color="warning"
          variant="text"
          aria-label="Back to User Menu"
          startIcon={<CompareArrows fontSize="small" sx={{ color: "primary.main" }} />}
          endIcon={<Close />}
          fullWidth={true}
          sx={{
            px: 2,
            py: 1,
            color: "text.primary",
            justifyContent: "flex-start",
            "& .MuiButton-endIcon": {
              ml: "auto",
            },
          }}
        >
          Switch client
        </Button>
      </Tooltip>
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <InputBase
          data-test={"client-menu-search"}
          placeholder="Search Clients..."
          fullWidth
          value={searchVal}
          onChange={(e: any) => setSearchVal(e.target.value)}
          endAdornment={<Search style={{ opacity: ".5" }} />}
        />
      </Box>
      <Divider />
      {filteredClients.map((client, index) => {
        return (
          <Button
            data-test={`client-menu-client${client.clientId}`}
            fullWidth={true}
            variant="text"
            key={index}
            onClick={() => {
              handleCloseClientsMenu();
              handleCloseUserMenu();
              handleSetClient(client);
            }}
            sx={{
              px: 2,
              py: 1,
              justifyContent: "flex-start",
              fontWeight: "normal",
              color: "text.primary",
              textTransform: "none",
            }}
          >
            {client.clientName}
          </Button>
        );
      })}
    </Box>
  );
};
